
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Policy } from "@/components/admin/contact-details/sections/policy/types";
import { LeadSourceType } from "@/types/leadSource";

interface UsePolicyFormProps {
  policy?: Policy | null;
  onSuccess?: () => void;
}

export const usePolicyForm = ({ policy, onSuccess }: UsePolicyFormProps = {}) => {
  // Initialize state with policy values if available
  const [producer, setProducer] = useState(policy?.producer_id || "");
  const [policyType, setPolicyType] = useState(policy?.insurance_type || "");
  const [insuranceCarrier, setInsuranceCarrier] = useState<string>(policy?.insurance_carrier || "");
  const [policyLine, setPolicyLine] = useState(policy?.policy_line || "");
  const [policyNumber, setPolicyNumber] = useState(policy?.policy_number || "");
  const [premiumAmount, setPremiumAmount] = useState<number>(policy?.premium_amount || 0);
  const [status, setStatus] = useState(policy?.status || "active");
  const [leadSource, setLeadSource] = useState<LeadSourceType>(
    (policy?.lead_source as LeadSourceType) || "Corporate Website"
  );
  const [startDate, setStartDate] = useState<Date>(policy?.start_date ? new Date(policy.start_date) : new Date());
  const [endDate, setEndDate] = useState<Date>(policy?.end_date ? new Date(policy.end_date) : new Date(new Date().setFullYear(new Date().getFullYear() + 1)));
  const [policyAcquisitionType, setPolicyAcquisitionType] = useState(policy?.policy_acquisition_type || "New Business");
  const { toast } = useToast();

  // Update initial values when policy changes
  useEffect(() => {
    if (policy) {
      console.log("usePolicyForm: Initializing with policy:", policy);
      
      // Update all form values when policy data changes
      setProducer(policy.producer_id || "");
      setPolicyType(policy.insurance_type || "");
      setInsuranceCarrier(policy.insurance_carrier || "");
      setPolicyLine(policy.policy_line || "");
      setPolicyNumber(policy.policy_number || "");
      setPremiumAmount(policy.premium_amount || 0);
      setStatus(policy.status || "active");
      
      // Make sure lead source is properly set
      if (policy.lead_source) {
        console.log("Setting lead source from policy:", policy.lead_source);
        setLeadSource(policy.lead_source as LeadSourceType);
      } else {
        setLeadSource("Corporate Website");
      }
      
      setPolicyAcquisitionType(policy.policy_acquisition_type || "New Business");
      
      if (policy.start_date) {
        setStartDate(new Date(policy.start_date));
      }
      
      if (policy.end_date) {
        setEndDate(new Date(policy.end_date));
      }
    }
  }, [policy]);

  const handlePolicyTypeChange = (value: string) => {
    console.log("Setting policy type to:", value);
    setPolicyType(value);
  };

  const isFormValid = producer && 
    policyType && 
    insuranceCarrier && 
    policyNumber && 
    leadSource &&
    (!["Personal", "Business", "Life"].includes(policyType) || policyLine);

  const handleSubmit = async () => {
    if (!isFormValid || !policy) return;

    try {
      // First, get the producer's name from profiles
      const { data: producerData, error: producerError } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', producer)
        .single();

      if (producerError) throw producerError;

      const producerName = producerData ? 
        `${producerData.first_name || ''} ${producerData.last_name || ''}`.trim() : 
        null;

      // Log all data being sent to Supabase for debugging
      const updateData = {
        producer_id: producer,
        producer_name: producerName,
        policy_line: policyLine,
        insurance_type: policyType,
        insurance_carrier: insuranceCarrier,
        policy_number: policyNumber,
        premium_amount: premiumAmount,
        status,
        lead_source: leadSource,
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
        policy_acquisition_type: policyAcquisitionType,
        updated_at: new Date().toISOString(),
      };
      
      console.log("Updating policy with data:", updateData);

      const { error } = await supabase
        .from('policies')
        .update(updateData)
        .eq('id', policy.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Policy updated successfully",
      });

      onSuccess?.();
    } catch (error) {
      console.error('Error updating policy:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update policy",
      });
    }
  };

  return {
    producer,
    policyType,
    insuranceCarrier,
    policyLine,
    policyNumber,
    premiumAmount,
    status,
    leadSource,
    startDate,
    endDate,
    policyAcquisitionType,
    setProducer,
    setPolicyType,
    setInsuranceCarrier,
    setPolicyLine,
    setPolicyNumber,
    setPremiumAmount,
    setStatus,
    setLeadSource,
    setStartDate,
    setEndDate,
    setPolicyAcquisitionType,
    handlePolicyTypeChange,
    handleSubmit,
    isFormValid
  };
};
